import { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Upload, X, Image as ImageIcon, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_SIZE = 2 * 1024 * 1024; // 2MB
const ALLOWED_EXTENSIONS = "PNG, JPG, WebP";

interface LogoUploadFieldProps {
  currentLogoUrl: string | null | undefined;
  onUpload: (file: File) => Promise<void>;
  onRemove: () => Promise<void>;
  isUploading?: boolean;
  isRemoving?: boolean;
  disabled?: boolean;
}

export function LogoUploadField({
  currentLogoUrl,
  onUpload,
  onRemove,
  isUploading = false,
  isRemoving = false,
  disabled = false,
}: LogoUploadFieldProps) {
  const [dragOver, setDragOver] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const displayUrl = previewUrl || currentLogoUrl;

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return `Tipo de arquivo não permitido. Use ${ALLOWED_EXTENSIONS}.`;
    }
    if (file.size > MAX_SIZE) {
      return "Arquivo excede o limite de 2MB.";
    }
    return null;
  };

  const handleFile = useCallback(async (file: File) => {
    const error = validateFile(file);
    if (error) {
      toast.error(error);
      return;
    }

    // Criar preview local imediato
    const localUrl = URL.createObjectURL(file);
    setPreviewUrl(localUrl);
    setImageError(false);

    try {
      await onUpload(file);
      // Limpar preview local após upload bem-sucedido (URL do S3 será usada)
      URL.revokeObjectURL(localUrl);
      setPreviewUrl(null);
    } catch {
      // Reverter preview em caso de erro
      URL.revokeObjectURL(localUrl);
      setPreviewUrl(null);
    }
  }, [onUpload]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    if (disabled || isUploading) return;

    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [disabled, isUploading, handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !isUploading) setDragOver(true);
  }, [disabled, isUploading]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset input para permitir re-upload do mesmo arquivo
    e.target.value = "";
  };

  const handleRemove = async () => {
    try {
      await onRemove();
      setPreviewUrl(null);
      setImageError(false);
    } catch {
      // Erro já tratado pelo caller
    }
  };

  const isProcessing = isUploading || isRemoving;

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-foreground">Logo do Canal</label>
      
      {displayUrl && !imageError ? (
        /* Preview da logo existente/carregada */
        <div className="relative group">
          <div className="w-full h-32 rounded-lg border border-border bg-muted/30 flex items-center justify-center overflow-hidden">
            <img
              src={displayUrl}
              alt="Logo do canal"
              className="max-h-28 max-w-full object-contain"
              onError={() => setImageError(true)}
            />
            {isUploading && (
              <div className="absolute inset-0 bg-background/60 flex items-center justify-center rounded-lg">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
                <span className="ml-2 text-sm text-foreground">Enviando...</span>
              </div>
            )}
          </div>
          {!disabled && !isProcessing && (
            <div className="flex gap-2 mt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                className="flex-1"
              >
                <Upload className="w-3.5 h-3.5 mr-1.5" />
                Substituir
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRemove}
                disabled={isRemoving}
                className="text-destructive hover:text-destructive"
              >
                {isRemoving ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <X className="w-3.5 h-3.5" />
                )}
                Remover
              </Button>
            </div>
          )}
        </div>
      ) : (
        /* Zona de drop / botão de upload */
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => !disabled && !isProcessing && fileInputRef.current?.click()}
          className={`
            w-full h-32 rounded-lg border-2 border-dashed transition-all cursor-pointer
            flex flex-col items-center justify-center gap-2
            ${dragOver ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-muted/30"}
            ${disabled || isProcessing ? "opacity-50 cursor-not-allowed" : ""}
            ${imageError ? "border-destructive/50" : ""}
          `}
        >
          {isUploading ? (
            <>
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Enviando logo...</span>
            </>
          ) : imageError ? (
            <>
              <AlertCircle className="w-8 h-8 text-destructive/60" />
              <span className="text-sm text-destructive/80">Erro ao carregar imagem</span>
              <span className="text-xs text-muted-foreground">Clique para enviar novamente</span>
            </>
          ) : (
            <>
              <ImageIcon className="w-8 h-8 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {dragOver ? "Solte a imagem aqui" : "Clique ou arraste a logo"}
              </span>
              <span className="text-xs text-muted-foreground">
                {ALLOWED_EXTENSIONS} · Máx. 2MB
              </span>
            </>
          )}
        </div>
      )}

      {/* Input oculto */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_TYPES.join(",")}
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled || isProcessing}
      />
    </div>
  );
}
